/**
 * Shared constants for the Historian page (djin-cloud#806).
 *
 * These live in their own module rather than in `HistorianContext` so the
 * presentational components (interval picker, preset row, colour swatches) can
 * import them without pulling the provider — and, more importantly, so there is
 * exactly ONE definition of each. The genieos original declared `CHART_COLORS`,
 * `IntervalOption` and `TimeRangePreset` inside the context file; two of those
 * were then needed by components, which is how a second copy starts.
 */

/**
 * Hard ceiling on series in one query.
 *
 * ⚠️ This is the CLIENT twin of `HISTORIAN_MAX_SERIES` in
 * `backend/src/services/historian-series.service.ts`. The server enforces its
 * own copy in `validateSpec`, so a divergence here does not let an oversized
 * spec through — it only produces a 400 the UI could have prevented. Change
 * both together.
 */
export const HISTORIAN_MAX_SERIES = 12;

/**
 * Chart series colours, in assignment order.
 *
 * Exactly `HISTORIAN_MAX_SERIES` entries, and that is load-bearing rather than
 * tidy: the assignment rule is "first colour not already in use", so a list
 * shorter than the series cap would hand two series the same colour at the top
 * of the range. genieos ships 10 here against a cap of 10; this file ships 12
 * against a cap of 12 for the same reason.
 */
export const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#e879f9', // fuchsia
] as const;

/**
 * Sample intervals the UI offers, as concrete shorthands (trimmed in djin-cloud#952).
 *
 * The server accepts more: its full `AUTO_INTERVAL_LADDER` (`1m`…`30d`) plus the
 * explicit-only `1s`. `auto` still resolves onto the full ladder, so dropping
 * `30m`/`6h`/`12h`/`7d`/`30d` from the picker removes a choice, never a result.
 * Every value here is accepted by the server; the narrowing is one-directional.
 */
export const EXPLICIT_INTERVALS = ['1s', '1m', '15m', '1h', '1d'] as const;

/** What the interval picker can hold. `auto` lets the server choose (~1500 points). */
export type IntervalOption = 'auto' | typeof EXPLICIT_INTERVALS[number];

/**
 * Longest range each FINE interval may be pinned over; intervals absent here are
 * unbounded (up to `MAX_RANGE_DAYS`).
 *
 * - `1s` — ⚠️ client twin of `EXPLICIT_ONLY_MAX_SPAN_MS` in
 *   `backend/src/services/historian-series.service.ts`. `1s` always reads the raw
 *   hypertable, which timed out at 60 s on a 24 h single-series read (data
 *   contract §9); the server rejects past 1 h, this copy fails the operator early.
 * - `1m` — UI-only. The server accepts `1m` at any range, but a pinned `1m` over
 *   weeks is the shape that truncates at the row budget and runs slow. 24 h is
 *   also the longest range `auto` itself resolves to `1m`, so this offers
 *   nothing `auto` would not.
 */
export const INTERVAL_MAX_SPAN_MS: Partial<Record<IntervalOption, number>> = {
  '1s': 3600e3,
  '1m': 86400e3,
};

/** Can `interval` be pinned over a range of `spanMs`? */
export function intervalAllowsSpan(interval: IntervalOption, spanMs: number): boolean {
  const max = INTERVAL_MAX_SPAN_MS[interval];
  return max === undefined || spanMs <= max;
}

/** Guidance shown next to each interval choice — sizing advice, not a constraint. */
export const INTERVAL_HINTS: Record<IntervalOption, string> = {
  auto: 'Automatically select based on time range (~1500 points)',
  '1s': 'Full resolution — ranges up to 1 hour',
  '1m': 'Ranges up to 1 day',
  '15m': 'Good for ranges up to 2 weeks',
  '1h': 'Good for ranges up to 2 months',
  '1d': 'Multi-month ranges',
};

export type TimeRangePreset =
  | 'last_15m'
  | 'last_1h'
  | 'last_6h'
  | 'last_24h'
  | 'last_7d'
  | 'last_30d'
  | 'custom';

/**
 * The preset buttons, with the lookback each one means.
 *
 * ⚠️ `ms` is a DURATION, not a stored boundary. A preset is re-evaluated
 * against the clock at execute time, so `last_1h` means "the hour before this
 * query runs", never "the hour before the button was pressed". That matters
 * for a spec restored from sessionStorage hours later: it must not replay a
 * stale window.
 */
export const TIME_RANGE_PRESETS: Array<{
  id: Exclude<TimeRangePreset, 'custom'>;
  label: string;
  ms: number;
}> = [
  { id: 'last_15m', label: '15m', ms: 900e3 },
  { id: 'last_1h', label: '1h', ms: 3600e3 },
  { id: 'last_6h', label: '6h', ms: 21600e3 },
  { id: 'last_24h', label: '24h', ms: 86400e3 },
  { id: 'last_7d', label: '7d', ms: 604800e3 },
  { id: 'last_30d', label: '30d', ms: 2592000e3 },
];

/**
 * Longest queryable span, in days.
 *
 * ⚠️ Client twin of `MAX_RANGE_MS` (`366 * 86400e3`) in
 * `historian-series.service.ts`. Same contract as `HISTORIAN_MAX_SERIES`: the
 * server is the enforcer, this copy exists to fail the operator early.
 */
export const MAX_RANGE_DAYS = 366;
